/**
 * Shared constants and styling for rate grid renderers.
 * These are used across all renderer components to ensure visual consistency.
 */

import { paletteVar } from "@platform/design-system";

import type { SystemStyleObject, Theme } from "@mui/system";

// Re-export label helpers from the single source of truth (rateFormatters)
// so renderers can import everything from "./constants".
export { getUnitLabel, getQuantityLabel } from "../../../utils/rateFormatters";

// Typography and sizing
export const FONT_SIZE: string = "14px";

// AG Grid CSS variable constants
export const AG_BORDER: string = "1px solid var(--ag-border-color, #babfc7)";
export const AG_HEADER_BG_COLOR: string =
  "var(--ag-header-background-color, #f5f7f7)";

// MUI's action.hover token. Its light value is the rgba(0,0,0,0.04) wash these
// inputs render today, so pointing at it keeps light unchanged and swaps to the
// dark scheme's light wash automatically. The read-only fields use a fainter
// idle tint, kept as the fallback.
const HOVER_WASH = paletteVar("action-hover", "rgba(0, 0, 0, 0.04)");
const IDLE_WASH = paletteVar("action-hover", "rgba(0, 0, 0, 0.02)");

// Shared input styling to match AG Grid
export const inputSx: SystemStyleObject<Theme> = {
  fontSize: FONT_SIZE,
  fontFamily: "inherit",
  padding: "4px 8px",
  width: "100%",
  height: "100%",
  textAlign: "right",
  // The app theme paints the InputBase root opaque white, which hides the grid
  // cell's scheme-aware background and leaves the (light) text unreadable on the
  // dark scheme. Stay transparent so the cell colour shows through; the hover
  // and focus washes below still read over it.
  backgroundColor: "transparent",
  "& input": {
    padding: 0,
    height: "100%",
    textAlign: "right",
  },
  "&:hover": {
    backgroundColor: HOVER_WASH,
  },
  "&.Mui-focused": {
    backgroundColor: "rgba(25, 118, 210, 0.08)",
  },
};

// Read-only input styling (for computed fields)
export const readOnlyInputSx: SystemStyleObject<Theme> = {
  ...inputSx,
  backgroundColor: IDLE_WASH,
  "&:hover": {
    backgroundColor: IDLE_WASH,
  },
  "& input": {
    padding: 0,
    height: "100%",
    textAlign: "right",
    cursor: "default",
  },
};

// Grid row styling (used with display: contents for CSS Grid)
export const gridRowSx: SystemStyleObject<Theme> = {
  display: "contents",
};

// Grid cell styling
export const gridCellSx: SystemStyleObject<Theme> = {
  display: "flex",
  alignItems: "center",
  borderBottom: AG_BORDER,
  borderRight: AG_BORDER,
};

// Header cell styling
export const headerCellSx: SystemStyleObject<Theme> = {
  ...gridCellSx,
  backgroundColor: AG_HEADER_BG_COLOR,
  fontSize: FONT_SIZE,
  fontFamily: "inherit",
  padding: "4px 8px",
};

// A row-label cell whose text must stay on one line (e.g. "Employee AD&D Rate per $1,000")
// clips instead of overflowing into the neighboring cell — visible as text bleeding through
// on hover, since the cell to the right paints an opaque background only until its own
// hover state turns semi-transparent. Pair with a `title` attribute carrying the full text.
export const truncatedLabelCellSx: SystemStyleObject<Theme> = {
  ...headerCellSx,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

export const SPACE_BETWEEN = "space-between";
